import SettingsAddUserModal from "@/components/SettingsAddUserModal/SettingsAddUserModal.vue";
import { sync_user_property_assignments } from "@/utils/user_property_assignments";
import {
    client_app,
    create_user_url,
    get_auth_headers,
    get_response_data,
    get_user_friendly_error_message
} from "../settings_user_management_api";

export default {
    name: "SettingsUserManagementAddUser",

    components: {
        SettingsAddUserModal
    },

    props: {
        is_open: {
            type: Boolean,
            default: false
        },
        users: {
            type: Array,
            default: () => []
        },
        role_options: {
            type: Array,
            default: () => []
        },
        property_options: {
            type: Array,
            default: () => []
        },
        is_loading_roles: {
            type: Boolean,
            default: false
        },
        is_loading_properties: {
            type: Boolean,
            default: false
        }
    },

    emits: ["close", "user-added", "reload-users"],

    data() {
        return ({
            is_saving: false,
            form_error: ""
        });
    },

    watch: {
        is_open(next_value) {
            if (next_value) {
                this.form_error = "";
            }
        }
    },

    methods: {
        close_modal() {
            this.form_error = "";
            this.$emit("close");
        },

        validate_user_payload(payload) {
            if (!payload.first_name || !payload.last_name || !payload.email) {
                return "First name, last name, and email are required.";
            }

            if (!payload.role_id) {
                return "Select a role for this user.";
            }

            if (!this.is_admin_role_id(payload.role_id) && !payload.property_ids?.length) {
                return "Select at least one property for this user.";
            }

            return "";
        },

        is_admin_role_id(role_id) {
            const matching_role = this.role_options.find((role) => String(role.id) === String(role_id));
            const role_name = String(matching_role?.name || "").toLowerCase();

            return role_name === "admin" || role_name === "administrator";
        },

        get_role_id(role) {
            return role?.id ?? null;
        },

        get_form_role_id(form_payload) {
            return form_payload.role_id ?? this.get_role_id(form_payload.role);
        },

        get_payload_property_ids(form_payload) {
            return Array.isArray(form_payload.property_ids)
                ? form_payload.property_ids.map((property_id) => Number(property_id))
                : null;
        },

        get_user_payload(form_payload) {
            const role_id = this.get_form_role_id(form_payload);
            const property_ids = this.get_payload_property_ids(form_payload);
            const email = String(form_payload.email || "").trim().toLowerCase();
            const payload = {
                first_name: form_payload.first_name,
                last_name: form_payload.last_name,
                email,
                phone_number: form_payload.phone_number ?? "",
                username: form_payload.username ?? email,
                is_active: form_payload.is_active !== false,
                profile_data: form_payload.profile_data || {},
                client_app
            };

            if (role_id) {
                payload.role_id = role_id;
            }

            if (property_ids) {
                payload.property_ids = property_ids;
            }

            return payload;
        },

        get_created_user_id(response_data) {
            return response_data?.id || null;
        },

        async save_user(form_payload) {
            const validation_error = this.validate_user_payload(form_payload);

            if (validation_error) {
                this.form_error = get_user_friendly_error_message(validation_error);
                return;
            }

            this.is_saving = true;
            this.form_error = "";

            try {
                const payload = this.get_user_payload(form_payload);
                const property_ids = payload.property_ids || [];
                let local_user = null;
                delete payload.property_ids;

                if (create_user_url) {
                    await this.save_user_to_api(payload, property_ids);
                } else {
                    local_user = this.get_local_user(form_payload, {
                        ...payload,
                        property_ids
                    });
                }

                this.$emit("user-added", "User added successfully.", local_user);
                this.close_modal();
            } catch (error) {
                this.form_error = get_user_friendly_error_message(error?.message, "We could not save this user. Please try again.");
            } finally {
                this.is_saving = false;
            }
        },

        async save_user_to_api(payload, property_ids) {
            const response = await fetch(create_user_url, {
                method: "POST",
                headers: get_auth_headers(true),
                body: JSON.stringify(payload)
            });
            const response_data = await get_response_data(response, "Unable to save user.");
            const created_user_id = this.get_created_user_id(response_data);

            if (!created_user_id && property_ids.length) {
                throw new Error("The user was created, but the backend did not return the created user's id.");
            }

            if (created_user_id && property_ids.length) {
                await sync_user_property_assignments(created_user_id, property_ids, get_auth_headers(true));
            }

            await this.emit_reload_users();
        },

        emit_reload_users() {
            return new Promise((resolve) => {
                this.$emit("reload-users", resolve);
            });
        },

        get_local_user(form_payload, payload) {
            return ({
                id: `local-${Date.now()}`,
                first_name: payload.first_name,
                last_name: payload.last_name,
                username: payload.username,
                email: payload.email,
                phone_number: payload.phone_number,
                role: form_payload.role?.name || "",
                role_id: payload.role_id,
                profile: payload.profile_data || {},
                session_policy: payload.profile_data?.session_policy || "default",
                session_timeout_minutes: payload.profile_data?.session_timeout_minutes ?? null,
                property_ids: payload.property_ids || [],
                assigned_properties: (payload.property_ids || []).map((property_id) => this.find_property_option(property_id)).filter(Boolean),
                is_active: payload.is_active
            });
        },

        find_property_option(property_id) {
            return this.property_options.find((property) => String(property.id) === String(property_id)) || null;
        }
    }
};
